/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes */
import { tracked } from '@glimmer/tracking';
import Controller from '@ember/controller';
import { action, computed } from '@ember/object';
import { alias } from '@ember/object/computed';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import { conformJurisdictionalId } from 'linced-app/utils/fields';
import { conformAmount, formatAmount } from 'linced-app/utils/finance';
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import PaymentMethod from '@linc-technologies/linced-common/enums/payment-method';
import { conformDate } from '@linc-technologies/linced-common/utils/strings';
import ENV from 'linced-app/config/environment';
import Papa from 'papaparse';

export default class FinancePiaController extends Controller {
  @service
  fetch;

  @service
  session;

  @service
  store;

  @alias('session.school.jurisdiction')
  jurisdiction;

  @tracked
  currentStep = 0;

  @action
  setCurrentStep(value) {
    this.currentStep = value;
  }

  fileTypes = Object.freeze(['text/csv', '.csv']);

  @tracked
  file = null;

  @action
  setFile(value) {
    this.file = value;
  }

  @computed('jurisdiction')
  get requiredColumns() {
    let jurisdictionalIdLabel = 'Student ID';
    if (this.jurisdiction === Jurisdiction.NZ_MOE) {
      jurisdictionalIdLabel = 'NSN';
    }

    return [
      { key: 'jurisdictionalId', label: jurisdictionalIdLabel },
      { key: 'date', label: 'Date' },
      { key: 'amount', label: 'Amount' },
      { key: 'method', label: 'Method' },
      { key: 'paidBy', label: 'Paid By' },
      { key: 'comment', label: 'Comment' },
    ];
  }

  @tracked
  fileHasCorrectColumns = false;

  @tracked
  columnCheckInProgress = true;

  @tracked
  columnMap = null;

  @tracked
  importProcessing = false;

  @tracked
  numPaymentsCreated = 0;

  @tracked
  numRowsSkipped = 0;

  @tracked
  numErrors = 0;

  @tracked
  numWarnings = 0;

  @tracked
  numRows = 0;

  @tracked
  missingJurisdictionalIds = null;

  @tracked
  seenHeader = false;

  ledgerMap = null;

  @tracked
  promiseChain = null;

  @tracked
  errors;

  @tracked
  warnings;

  @computed('model.{accounts.[],learners.[]}')
  get accountMap() {
    return this.model.accounts.reduce((map, account) => {
      let jurisdictionalId = account.get('person.jurisdictionalId');
      if (!isEmpty(jurisdictionalId)) {
        map[jurisdictionalId] = account;
      }
      return map;
    }, {});
  }

  get importing() {
    if (this.importProcessing) {
      return true;
    }
    let numDone =
      this.numRowsSkipped + this.numPaymentsCreated + this.numErrors;
    return numDone !== this.numRows;
  }

  handleParsedColumns(results) {
    let headers = results.data[0];
    let map = {};
    let hasAllColumns = this.requiredColumns.every((col) => {
      let index = headers.indexOf(col.label);
      if (index === -1) {
        return false;
      }
      map[col.key] = index;
      return true;
    });

    this.fileHasCorrectColumns = hasAllColumns;
    this.columnCheckInProgress = false;
    this.columnMap = hasAllColumns ? map : {};
  }

  importRow(row) {
    // Skip header row.
    if (!this.seenHeader) {
      this.seenHeader = true;
      return;
    }

    // Skip empty rows.
    let data = row.data;
    if (isEmpty(data) || data.every((cell) => isEmpty(cell))) {
      return;
    }

    this.incrementProperty('numRows');

    let map = this.columnMap;

    // Check if we have a jurisdictional ID.
    let jurisdictionalId = data[map.jurisdictionalId].trim();
    jurisdictionalId = conformJurisdictionalId(
      this.jurisdiction,
      jurisdictionalId,
    );
    if (isEmpty(jurisdictionalId)) {
      this.errors = [...this.errors, { message: 'Missing student ID', data }];
      this.incrementProperty('numErrors');
      return;
    }

    // Check if we have an account in the map for this jurisdictional ID.
    let account = this.get(`accountMap.${jurisdictionalId}`);
    if (isEmpty(account)) {
      if (!this.missingJurisdictionalIds.includes(jurisdictionalId)) {
        this.missingJurisdictionalIds = [
          ...this.missingJurisdictionalIds,
          jurisdictionalId,
        ];
      }
      this.incrementProperty('numRowsSkipped');
      return;
    }

    // Validate the incoming data.
    let method = PaymentMethod.getValue(data[map.method].trim());
    if (isEmpty(method)) {
      this.errors = [
        ...this.errors,
        { message: 'Unknown payment method', data },
      ];
      this.incrementProperty('numErrors');
      return;
    }

    let amount = formatAmount(conformAmount(data[map.amount]));
    if (amount < 0.01) {
      // If the amount is zero or negative, log it as an error and move on.
      this.errors = [
        ...this.errors,
        { message: 'Amount must be at least $0.01', data },
      ];
      this.incrementProperty('numErrors');
      return;
    }

    let date = conformDate(data[map.date]);
    if (isEmpty(date)) {
      this.errors = [
        ...this.errors,
        { message: 'Date is missing or invalid', data },
      ];
      this.incrementProperty('numErrors');
      return;
    }

    // Make the payment.
    let url = `${ENV.APP.apiHost}/finance/${ENV.APP.apiPath}/accounts/${account.id}/:pay`;
    let payload = {
      accountId: parseInt(account.id, 10),
      method,
      personName: data[map.paidBy],
      comment: data[map.comment],
      date,
      amount: Math.round(amount * 100),
    };
    let options = {
      data: JSON.stringify(payload),
    };
    this.promiseChain = this.promiseChain.then(() =>
      this.fetch
        .post(url, options)
        .then(() => {
          this.incrementProperty('numPaymentsCreated');
        })
        .catch(() => {
          this.incrementProperty('numErrors');
          this.errors = [
            ...this.errors,
            { message: 'Failed to create Payment in Advance', data },
          ];
        }),
    );
  }

  importComplete() {
    this.promiseChain.finally(() => {
      this.importProcessing = false;
    });
  }

  @action
  checkColumns() {
    this.fileHasCorrectColumns = false;
    this.columnCheckInProgress = true;
    this.columnMap = {};
    Papa.parse(this.file.data, {
      header: false,
      preview: 1,
      complete: this.handleParsedColumns.bind(this),
    });
  }

  @action
  import() {
    this.importProcessing = true;
    this.numPaymentsCreated = 0;
    this.numRows = 0;
    this.numRowsSkipped = 0;
    this.numErrors = 0;
    this.numWarnings = 0;
    this.errors = [];
    this.warnings = [];
    this.missingJurisdictionalIds = [];
    this.seenHeader = false;
    this.promiseChain = Promise.resolve();

    Papa.parse(this.file.data, {
      header: false,
      step: this.importRow.bind(this),
      complete: this.importComplete.bind(this),
    });
  }

  reset() {
    this.currentStep = 0;
    this.file = null;
    this.fileHasCorrectColumns = false;
    this.columnCheckInProgress = true;
    this.columnMap = {};
    this.importProcessing = false;
    this.numPaymentsCreated = 0;
    this.numRowsSkipped = 0;
    this.numErrors = 0;
    this.numWarnings = 0;
    this.errors = [];
    this.warnings = [];
    this.missingJurisdictionalIds = [];
    this.seenHeader = false;
  }
}
